// Libraries
import React, { Component } from 'react';
import hash from 'object-hash';
import Swal from 'sweetalert2';
import { getUrl } from '../globals/url';

// Components
import BootstrapContainer from '../globals/bootstrap-container';
import Map from '../globals/google-map/map';
import RoadInput from '../globals/form-fields/road-input';
import Input from '../globals/form-fields/input';
import Checkbox from '../globals/form-fields/checkbox';
import ImageUpload from '../globals/form-fields/image-upload';
import RoadReasonDropdown from '../globals/form-fields/road-reason-dropdown';
import { getPostalCode } from '../abstract/maps';
import {
    scrollToTargetAdjusted,
    runThroughValidation,
    highlightRequiredField,
    bundleData,
} from './vote-form-actions';
import { manageSession, markStepsIncomplete } from '../abstract/sessions';
import { getCurrentLang, translate } from '../globals/translations';
import {
    submitRoadPhoto,
    shouldThrottleVote,
    getApiKey,
} from '../abstract/api';
import { getCurrentDate } from '../abstract/time';
import * as clubUtils from '../globals/club';

const recaptchaRef = React.createRef();

/**
 * VoteForm
 *
 * Parent class component that renders the step through forms
 *
 * @since   0.5
 * @package app\vote-form
 */
class StepOne extends Component {
    constructor(props) {
        super(props);

        this.state = {
            additionalComments: '',
            audience_targeting: '',
            city: '',
            clubData: null,
            crossroadDisabled: false,
            crossroadObject: {
                address: '',
                location: {
                    lat: '',
                    lng: '',
                },
            },
            endpoint: '',
            disabled: true,
            displayMap: true,
            email: '',
            honeypotFieldName: '',
            images: [],
            inputDisabled: false,
            manualVote: false,
            markerLocation: null,
            otherReasons: '',
            roadObject: {
                address: '',
            },
            privacy_policy: '',
            province: '',
            roadProblems: [],
            submitBtnText: translate('vote_now'),
            ts: Date.now(),
            validationTimeoutId: null,
        };

        this.maxImagesLength = 5;

        this.onRoadUpdate = this.onRoadUpdate.bind(this);
        this.onCrossroadUpdate = this.onCrossroadUpdate.bind(this);
        this.submitForm = this.submitForm.bind(this);
        this.handleResponse = this.handleResponse.bind(this);
        this.setImages = this.setImages.bind(this);
        this.sendToAPI = this.sendToAPI.bind(this);

        // actions to take after token value changes ( ReCaptcha )
        this.handleOnChange = this.handleOnChange.bind(this);

        // actions to take after road problem values change
        // from Child Component
        this.handleReasonsChange = this.handleReasonsChange.bind(this);
        this.handleOtherReasonsChange =
            this.handleOtherReasonsChange.bind(this);
    }

    componentDidMount() {
        this.registerManualVoteOpenListener();
        this.registerManualVoteCloseListener();
    }

    /**
     * Click listener for revealing the manual vote modal
     */
    registerManualVoteOpenListener() {
        let $modal = $('#manualVoteModal');
        let $manualVoteBtn = $('#manualVote');

        if ($modal.find('iframe').length) {
            $manualVoteBtn.on('click', (event) => {
                event.preventDefault();

                $modal.modal('show');

                event.stopPropagation();
            });
        } else {
            $manualVoteBtn.parent().remove();
        }
    }

    /**
     * Click listener for closing the manual vote modal
     */
    registerManualVoteCloseListener() {
        $(document).on('click', '#manualVoteModal', function (event) {
            if (!$(event.target).closest('.modal-content-wrapper').length) {
                $('#manualVoteModal').modal('hide');
            }
        });
    }

    /**
     * onRoadUpdate
     *
     * Callback function used to set a browser session(road) which stores
     * the address object from Google Maps API. This method also sets
     * the map marker position and the address used for filling up
     * the roadNameInput field.
     *
     * @since 0.5
     *
     * @param {object} || {string} roadObject Google Maps API return or User inputted string
     * @return {n/a}
     *
     */
    onRoadUpdate(roadObject) {
        const roadNameInputField = document.getElementById('roadName');

        // Check if the road was found, if not, use geoLocation lookup
        if (typeof roadObject == 'object') {
            // Clear all timeouts that were set for validation
            if (this.state.validationTimeoutId !== null) {
                clearTimeout(this.state.validationTimeoutId);
            }

            if (!roadNameInputField.classList.contains('-hidden')) {
                roadNameInputField.classList.add('-hidden');
            }

            this.setState({
                crossroadDisabled: false,
                inputDisabled: false,
                markerLocation: roadObject.location,
                manualVote: false,
                roadObject: roadObject,
                validationTimeoutId: null,
            });

            if (roadObject.data.postal_code == undefined) {
                this.setState({ disabled: true });

                /**
                 * Some roads from google do not comeback with a postal code. If that's the case,
                 * we run the getPostalCode() to go look for that postal code.
                 */
                getPostalCode(roadObject, this);
            }

            if (roadObject.data.province !== '') {
                this.setState({ province: roadObject.data.province });
            }

            if (roadObject.data.locality !== '') {
                this.setState({ city: roadObject.data.locality });
            }
        } else {
            /**
             * If we get here, user did not select a road from the dropdown.
             */
            let timeoutId = null;

            // Delaying slightly to give auto-populate dropdown a moment to show up
            if (
                roadNameInputField.classList.contains('-hidden') &&
                this.state.validationTimeoutId == null
            ) {
                timeoutId = setTimeout(() => {
                    roadNameInputField.classList.remove('-hidden');
                }, 2000);

                this.setState({ validationTimeoutId: timeoutId });
            }

            this.setState({
                crossroadDisabled: true,
                disabled: true,
                inputDisabled: true,
                markerLocation: null,
                roadObject: { address: '' },
            });
        }
    }

    setImages(images) {
        this.setState({
            images,
        });
    }

    /**
     * onCrossroadUpdate
     *
     * Callback function used to set a browser session(crossroad) which stores
     * the address object from Google Maps API and the form input for the
     * nearestCrossroadInput field.
     *
     * @since 0.5
     *
     * @param {object} || {string} crossroad Google Maps API return or input value string
     * @return {n/a}
     *
     */
    onCrossroadUpdate(crossroad, intersectionObject, validation) {
        if (typeof crossroad == 'object') {
            this.setState({
                crossroadObject: {
                    address: crossroad.address,
                    location: {
                        lat: crossroad.location.lat,
                        lng: crossroad.location.lng,
                    },
                },
                markerLocation: intersectionObject,
            });
        } else {
            this.setState({
                crossroadObject: {
                    address: crossroad,
                },
            });
        }
    }

    /**
     * componentWillMount
     *
     * This method runs before this component is rendered(mounted) to the DOM.
     * Used to check if a user sessions exists and sets component state.
     *
     * @param {n/a}
     * @return {n/a}
     *
     */
    UNSAFE_componentWillMount() {
        let stepOneSession = sessionStorage.getItem('stepOneSession');
        const timestamp = Math.round(new Date().getTime() / 1000);
        let randomString = `honeypost_field_${timestamp}`;
        randomString = hash.MD5(randomString);

        const clubData = JSON.parse(this.props.club);
        const baseUrl = getUrl();
        const api_endpoint = `${baseUrl}/api/vote/submit`;

        const url = 'maps.googleapis.com/maps/api/';
        const scripts = document.getElementsByTagName('script');
        let is_api_loaded = false;

        if (clubData.links.privacy_policy == undefined) {
            clubData.links.privacy_policy = '';
        }

        if (clubData.links.contest_rules_url == undefined) {
            clubData.links.contest_rules_url = '';
        }

        // Check if stepOneSession isset and use it to fillout form
        if (stepOneSession != null) {
            stepOneSession = JSON.parse(stepOneSession);

            // Update session data
            stepOneSession.completed = false;
            const updateSessionData = JSON.stringify(stepOneSession);
            sessionStorage.setItem('stepOneSession', updateSessionData);
            markStepsIncomplete();

            stepOneSession.road = JSON.parse(stepOneSession.road);
            stepOneSession.crossroad = JSON.parse(stepOneSession.crossroad);

            if (
                typeof stepOneSession.marker != null &&
                stepOneSession.marker != ''
            ) {
                stepOneSession.marker = JSON.parse(stepOneSession.marker);
            } else {
                stepOneSession.marker = stepOneSession.marker;
            }

            this.setState({
                additionalComments: stepOneSession.additionalComments,
                audience_targeting: stepOneSession.audience_targeting,
                city: stepOneSession.city,
                crossroadDisabled: false,
                crossroadObject: stepOneSession.crossroad,
                disabled: false,
                email: stepOneSession.email,
                inputDisabled: false,
                manualVote: false,
                markerLocation: stepOneSession.marker,
                privacy_policy: stepOneSession.privacy_policy,
                province: stepOneSession.province,
                roadObject: stepOneSession.road,
            });
        }

        // Check if road id isset and use it to fillout form, instead of stepOneSession
        if (this.props.road != null) {
            const roadData = JSON.parse(this.props.road);

            if (typeof roadData != null) {
                // If no road lat long then use the clubs default lat long
                const markerPosition =
                    roadData.markerLocation.lat && roadData.markerLocation.lng
                        ? roadData.markerLocation
                        : clubData.map_data.location;
                let road = '';

                if (typeof roadData.roadObject == 'string') {
                    road = JSON.parse(roadData.roadObject);
                } else {
                    road = roadData.roadObject;
                }

                this.setState({
                    city: roadData.city,
                    crossroadDisabled: false,
                    disabled: false,
                    inputDisabled: false,
                    manualVote: roadData.method == 'lookup' ? false : true,
                    markerLocation: markerPosition,
                    province: roadData.province,
                    roadObject: road,
                });
            }
        }

        for (let i = scripts.length; i--; ) {
            if (scripts[i].src.includes(url)) {
                is_api_loaded = true;

                break;
            }
        }

        this.setState({
            clubData: clubData,
            endpoint: api_endpoint,
            displayMap: is_api_loaded,
            honeypotFieldName: randomString,
        });

        if (this.props.lastStep != '1') {
            this.setState({ submitBtnText: translate('vote_now') });
        }
    }

    /**
     * Submit Form
     *
     * Callback method that runs when the step one vote form
     * gets submitted
     *
     * @param {object} evt Event object
     * @return {n/a}
     *
     */
    submitForm(evt) {
        evt.preventDefault();

        const data = bundleData(this.state, this.props);
        const form = document.getElementById('stepOneFrom');
        const response = runThroughValidation(form, 1, data, this.state.images);

        // Handle when form is not good for submission
        if (!response.completed) {
            this.handleResponse(response);

            const errorElements = document.querySelectorAll(
                '.form-control.error'
            );

            if (errorElements.length != 0) {
                scrollToTargetAdjusted(errorElements[0]);
            }

            return null;
        }

        this.formLoadingState('activate');

        if (shouldThrottleVote()) {
            // The submitted a vote within 5 seconds from each other. Need
            // to delay THIS next one as per client business rules. And
            // let the API know is it can mark it as a failed vote.
            response.data.vote_frequency_validation_failed = true;

            setTimeout(() => {
                this.sendToAPI(response.data, this.handleResponse);
            }, 2000);
        } else {
            // We are safe to go ahead and submit vote
            response.data.vote_frequency_validation_failed = false;

            this.sendToAPI(response.data, this.handleResponse);
        }
    }

    formLoadingState(status) {
        const form_loader_container = document.getElementById('form-loader');

        if (status == 'activate') {
            // Show loading state
            form_loader_container.style.display = 'block';

            setTimeout(() => {
                form_loader_container.classList.add('-show');
            }, 200);

            return;
        }

        // Close loading state
        form_loader_container.style.display = 'none';
        form_loader_container.classList.remove('-show');
    }

    /**
     * Send to API
     *
     * Send a vote to the API.
     *
     * @since 0.5
     *
     * @param {object} data The data that we need to submit to API
     * @param {object} handleResponse Object to handle the ajax response
     * @return {n/a}
     *
     */
    sendToAPI(data, handleResponse) {
        let _this = this; // store in order to acesss inside ajax

        let errorResponse = {
            status: '',
        };

        data.key = getApiKey();

        $.ajax({
            method: 'POST',
            url: this.state.endpoint,
            data: data,
            crossDomain: true,
            mimeType: 'multipart/form-data',
            statusCode: {
                400: () => {
                    errorResponse.status = 400;
                    handleResponse(errorResponse, data);
                },
                404: () => {
                    errorResponse.status = 404;
                    handleResponse(errorResponse, data);
                },
                500: () => {
                    handleResponse(errorResponse, data);
                },
            },
        }).done((res) => {
            res = JSON.parse(res);

            const date = getCurrentDate();

            sessionStorage.setItem('lastVotedOn', date.unix);

            if (_this.state.images.length > 0) {
                Swal.fire({
                    title: 'Uploading photos...',
                    onBeforeOpen: () => {
                        Swal.showLoading();
                    },
                });

                submitRoadPhoto(_this.state.images, res, data, handleResponse);
            } else {
                handleResponse(res, data);
            }

            // trigger custom GTM event for successful submissions
            window.dataLayer = window.dataLayer || [];

            window.dataLayer.push({ event: 'wrVoteFormSubmission' });
        });
    }

    /**
     * handleResponse
     *
     * Method that runs and processes the form submission
     * response
     *
     * @param {object} res Form submission response
     * @param {object} data Data that was sent to the API
     *
     * @return {n/a}
     *
     */
    handleResponse(res, data) {
        // Handle all the response code
        switch (res.status) {
            case 445: // Too many images in request
                console.log('Error 445: Form has too many images.');

                let swalTitle = translate('image_max');
                let swalMessage = translate('please_remove_image');

                Swal.fire({
                    icon: 'error',
                    title: swalTitle,
                    text: swalMessage,
                    button: false,
                    showCloseButton: true,
                    showConfirmButton: false,
                });

                this.formLoadingState('deactivate');

                break;
            case 444: // User submitted a form with missing params
                console.log('Error 444: Form validation failed.');

                highlightRequiredField(res.form);

                break;
            case 400: // We sent a request to the wrong endpoint
                console.log(
                    'Error 400: Sent a request to the wrong endpoint or missing a param.'
                );
                console.log(res.messages);

                if (res.invalid_email_message) {
                    let swalTitle = translate('invalid_email');
                    let swalMessage =
                        getCurrentLang() == 'fr-CA'
                            ? res.invalid_email_message_fr
                            : res.invalid_email_message;

                    Swal.fire({
                        icon: 'error',
                        title: swalTitle,
                        text: swalMessage,
                        button: false,
                        showCloseButton: true,
                        showConfirmButton: false,
                    });

                    this.formLoadingState('deactivate');
                }

                break;
            default:
                if (this.props.lastStep == '1') {
                    let swalTitle = translate('success');
                    let swalMessage = translate('info_submitted');

                    Swal.fire({
                        icon: 'success',
                        title: swalTitle,
                        text: swalMessage,
                        button: false,
                        showCloseButton: true,
                        showConfirmButton: false,
                    });

                    setTimeout(() => {
                        manageSession(
                            'stepOneSession',
                            data,
                            this.props.currentStep,
                            this.props.lastStep,
                            res
                        );
                    }, 800);
                } else {
                    manageSession(
                        'stepOneSession',
                        data,
                        this.props.currentStep,
                        this.props.lastStep,
                        res
                    );
                }

                break;
        }
    }

    /**
     * Render the Google Maps based on the state
     */
    renderMap() {
        if (this.state.displayMap) {
            return (
                <div className="map vote-info">
                    <Map
                        mapCoordinates={this.state.clubData.map_data}
                        markerCoordinates={this.state.markerLocation}
                    />
                </div>
            );
        } else {
            return null;
        }
    }

    /**
     * Decode HTML entities in strings from clubData
     */
    htmlDecode(input) {
        var doc = new DOMParser().parseFromString(input, 'text/html');

        return doc.documentElement.textContent;
    }

    /**
     * Render form messaging — set by the club
     */
    renderFormMessage() {
        return (
            <div className="svg-text-inline">
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="26"
                    height="22"
                    viewBox="0 0 26 22"
                    fill="none"
                    focusable="false"
                    aria-hidden="true"
                    tabindex="-1"
                >
                    <path
                        fill="#fff"
                        d="M13 .5c.656 0 1.266.375 1.594.938l10.125 17.25c.328.609.328 1.312 0 1.875-.328.609-.938.937-1.594.937H2.875c-.703 0-1.312-.328-1.64-.937-.329-.563-.329-1.266 0-1.875l10.124-17.25A1.89 1.89 0 0 1 13 .5m0 6a1.11 1.11 0 0 0-1.125 1.125v5.25c0 .656.469 1.125 1.125 1.125a1.11 1.11 0 0 0 1.125-1.125v-5.25A1.14 1.14 0 0 0 13 6.5M14.5 17c0-.797-.703-1.5-1.5-1.5-.844 0-1.5.703-1.5 1.5 0 .844.656 1.5 1.5 1.5.797 0 1.5-.656 1.5-1.5"
                    />
                </svg>
                <p>{this.htmlDecode(this.state.clubData?.form_message)}</p>
            </div>
        );
    }

    /**
     * Render Manual Vote text
     */
    renderManualVoteMessage() {
        return (
            <div className="svg-text-inline">
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    focusable="false"
                    aria-hidden="true"
                    tabindex="-1"
                >
                    <path
                        fill="#fff"
                        d="m22.078 1.031.89.89a2.603 2.603 0 0 1 0 3.704l-1.405 1.406-4.594-4.593 1.406-1.407a2.603 2.603 0 0 1 3.703 0M8.063 11.344l7.828-7.828 4.593 4.593-7.828 7.829a3 3 0 0 1-1.031.656l-4.172 1.36c-.375.14-.844.046-1.125-.282a1.03 1.03 0 0 1-.281-1.125l1.36-4.172c.14-.375.374-.75.656-1.031M4.5 3H9c.797 0 1.5.703 1.5 1.5C10.5 5.344 9.797 6 9 6H4.5C3.656 6 3 6.703 3 7.5v12c0 .844.656 1.5 1.5 1.5h12c.797 0 1.5-.656 1.5-1.5V15c0-.797.656-1.5 1.5-1.5.797 0 1.5.703 1.5 1.5v4.5c0 2.484-2.016 4.5-4.5 4.5h-12A4.5 4.5 0 0 1 0 19.5v-12C0 5.016 2.016 3 4.5 3"
                    />
                </svg>
                <p>
                    {translate('manual_submit')}
                    <a href="" id="manualVote">
                        {translate('manual_vote')}
                    </a>
                </p>
            </div>
        );
    }

    /**
     * Render form heading based on locale.
     *
     * @returns string
     */
    renderFormHeading() {
        return translate('road_select');
    }

    /**
     * Render vote heading and messaging - set by the club
     *
     * @returns string
     */
    renderVoteHeadingMessaging() {
        const { clubData } = this.state;

        const vote_heading = clubData?.vote_heading ? (
            <p>
                <strong>{clubData.vote_heading}</strong>
            </p>
        ) : null;

        const vote_message = clubData?.vote_message ? (
            <p>{clubData.vote_message}</p>
        ) : null;

        return (
            <div className="vote-information">
                {this.htmlDecode(vote_heading)}
                {this.htmlDecode(vote_message)}
            </div>
        );
    }

    /**
     * callback for onChange event
     *
     * Let us keep track of the validated token for the ReCaptcha
     *
     * @param {*} value
     * @see ReCAPTCH
     */
    handleOnChange(value) {
        if (!value) {
            recaptchaRef.current.value = null;
        } else if (this.state.verifiedToken !== value) {
            recaptchaRef.current.value = value;
        }

        // let's force update the timestamp so that just assigned ref values do take effect
        this.setState({ ts: Date.now() });
    }

    handleCrossroadDisabled = (disabled) => {
        this.setState({ crossroadDisabled: disabled });
    };

    /**
     * callback for onChange event for Road Problems Select
     *
     * This is initiated from the child component SelectUnsafeMultiple
     *
     * @param {*} value -> selectedOptions (this is to keep the state and pass the previous saved session data to the child component)
     * @param {*} valueToSave -> optionsToSave (this is to use as an array of values to write to the database)
     */
    handleReasonsChange(reasons) {
        this.setState({ roadProblems: reasons }, () => {});
    }

    handleOtherReasonsChange(other_reasons) {
        this.setState({ otherReasons: other_reasons }, () => {});
    }

    /**
     * Callback for onClick of vote submit button
     * Verifies the reCAPTCHA token before submitting the form
     * @param {*} e event
     */
    async handleSubmit(e) {
        const { googleReCaptchaProps } = this.props;

        if (!googleReCaptchaProps || !googleReCaptchaProps.executeRecaptcha) {
            return;
        }

        try {
            // Generate the reCAPTCHA token
            const recaptchaToken = await googleReCaptchaProps.executeRecaptcha(
                'submitVote'
            );

            const response = await fetch('/wp-json/wr/v1/verify-recaptcha', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ recaptchaToken }),
            });

            const result = await response.json();
            const recaptchaThreshold = 0.5;

            if (result.success && result.score) {
                if (result.score < recaptchaThreshold) {
                    Swal.fire({
                        title: 'Recaptcha Failed',
                        text: 'Recaptcha score is too low. Please try again.',
                        icon: 'error',
                        confirmButtonText: 'OK',
                    });
                    return;
                } else {
                    // Proceed with form submission
                    this.submitForm(e);
                }
            }
        } catch (error) {
            console.error('Error executing reCAPTCHA:', error);
        }
    }

    /**
     * render
     *
     * Return the correct form for step one and map
     *
     * @since 0.5
     *
     * @param {n/a}
     * @return {jsx}
     *
     */
    render() {
        const provinceCheck = this.state.clubData?.map_data?.province[0];

        return (
            <section className="vote-form-section">
                <BootstrapContainer containerClass="vote-form-container">
                    <div className="col-xs-10 col-sm-8 col-xs-offset-1 col-sm-offset-2 vote-form">
                        <h2 className="select-road">
                            {this.renderFormHeading()}
                        </h2>

                        <form
                            id="stepOneFrom"
                            onSubmit={(evt) => evt.preventDefault()}
                            autoComplete="off"
                        >
                            {this.state.clubData?.form_message &&
                                this.renderFormMessage()}

                            {this.renderManualVoteMessage()}

                            <Input
                                containerClass="honey-pot-container"
                                inputName={this.state.honeypotFieldName}
                                inputPlaceholder="Honey Pot"
                                inputType="hidden"
                                ariaHidden={true}
                            />

                            <RoadInput
                                onRoadUpdate={this.onRoadUpdate}
                                onCrossroadUpdate={this.onCrossroadUpdate}
                                roadObject={this.state.roadObject}
                                crossroadObject={this.state.crossroadObject}
                                roadValue={this.state.roadObject.address}
                                crossroadValue={
                                    this.state.crossroadObject.address
                                }
                                crossroadDisabled={this.state.crossroadDisabled}
                                onCrossroadDisabledUpdate={
                                    this.handleCrossroadDisabled
                                }
                                isApiLoaded={this.state.displayMap}
                            />

                            <Input
                                containerClass="additional-comments-container"
                                inputType="text"
                                inputName="additional_comments"
                                inputPlaceholder={translate(
                                    'additional_comments_placeholder'
                                )}
                                labelText={`${translate(
                                    'additional_comments_label'
                                )} ${translate('optional')}`}
                                value={this.state.additionalComments}
                                isRequired={false}
                                inputDisabled={this.state.inputDisabled}
                                helperText={translate(
                                    'additional_comments_helper_text'
                                )}
                            />

                            <RoadReasonDropdown
                                inputDisabled={this.state.inputDisabled}
                                reasons={this.props.reasons}
                                onReasonsChange={(selectedReasons) =>
                                    this.handleReasonsChange(selectedReasons)
                                }
                                onOtherReasonsChange={
                                    this.handleOtherReasonsChange
                                }
                                provinceCheck={provinceCheck}
                            />

                            <Input
                                containerClass="email-container"
                                inputType="text"
                                inputName="email"
                                inputPlaceholder={translate(
                                    'email_placeholder'
                                )}
                                labelText={translate('email_label') + ' *'}
                                value={this.state.email}
                                isRequired={true}
                                inputDisabled={this.state.inputDisabled}
                                autoComplete={'email'}
                            />

                            <ImageUpload
                                images={this.state.images}
                                setImages={this.setImages}
                                maxImagesLength={this.maxImagesLength}
                            />

                            {(this.state.clubData?.vote_heading ||
                                this.state.clubData?.vote_message) &&
                                this.renderVoteHeadingMessaging()}

                            <Checkbox
                                name="privacy_policy"
                                value={this.state.privacy_policy}
                                contest_url={
                                    this.state.clubData.links.contest_rules_url
                                }
                                privacy_url={
                                    this.state.clubData.links.privacy_policy_url
                                }
                            />

                            {clubUtils.audienceTargetingEnabled(
                                parseInt(this.props.clubId)
                            ) === true && (
                                <Checkbox
                                    name="audience_targeting"
                                    labelText={translate('consent_targeting')}
                                    value={this.state.audience_targeting}
                                />
                            )}

                            <button
                                type="button"
                                id="step-one-submit-btn"
                                className="btn btn-primary"
                                disabled={this.state.disabled}
                                onClick={(e) => this.handleSubmit(e)}
                            >
                                {this.state.submitBtnText}
                            </button>
                        </form>
                    </div>
                </BootstrapContainer>

                {this.renderMap()}
            </section>
        );
    }
}

export default StepOne;
